/**
 * Объяснение, которое открывается, а не наводится.
 *
 * Почему это компонент, а не `title=`. Нативная подсказка недоступна с
 * клавиатуры и на сенсорном экране, её нельзя выделить и скопировать, она
 * исчезает через несколько секунд и не переносится по строкам. Пока она
 * украшает и без того понятную кнопку — это мелочь; когда в ней живёт
 * ЕДИНСТВЕННОЕ объяснение смысла (что входит в знаменатель, почему «без
 * отчёта» — не ноль), половина людей это объяснение просто не получает.
 *
 * Правило, которое компонент удерживает: **объяснение не живёт в `title`**.
 * `title` остаётся допустимым для короткого повтора уже видимого текста —
 * например, точного значения округлённого числа, — но не для того, без чего
 * величина читается неверно.
 *
 * Что здесь не делается намеренно:
 *
 * * это НЕ тултип по наведению. Popover открывается нажатием или клавишей,
 *   закрывается Escape и возвращает фокус на кнопку — иначе он был бы тем же
 *   `title`, только нарисованным своими руками;
 * * это НЕ место для команд. Внутри — текст и ссылки; кнопка, меняющая
 *   данные, требует диалога с явным закрытием, а не всплывающей подсказки.
 */

import { CircleHelp, Info } from "lucide-react";
import type { ReactNode } from "react";
import {
  Button,
  ButtonContext,
  Dialog,
  DialogTrigger,
  Popover,
} from "react-aria-components";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import styles from "./controls.module.css";

export const FIELD_TEXT = defineMessages({
  valueFallback: { id: "common.kit.field.valueFallback", defaultMessage: "значение", description: "Запасное имя поля (список, выбор, ввод) для экранных дикторов, когда подписи-строки нет; строчная" },
});

const m = defineMessages({
  explain: { id: "common.kit.info.explain", defaultMessage: "Пояснение: {label}", description: "aria-label кнопки-знака вопроса рядом с подписью; {label} — что объясняется (предмет), чтобы кнопки в списке не звучали одинаково" },
});

export function InfoPopover({
  /** Что объясняем. Уходит в имя кнопки для чтения вслух и в заголовок. */
  label,
  children,
  /** Заголовок внутри окна. По умолчанию — то же, что объясняем. */
  heading,
  /** Значок: «?» — объяснение смысла, «i» — справка о методике (agroai-0yt.2). */
  icon = "help",
}: {
  label: string;
  children: ReactNode;
  heading?: ReactNode;
  icon?: "help" | "info";
}) {
  const t = useT();
  const Glyph = icon === "info" ? Info : CircleHelp;
  return (
    /*
     * Сброс контекста кнопки — не украшение и не перестраховка.
     *
     * `Select`, `ComboBox` и `DatePicker` раздают свои триггерные свойства
     * ЛЮБОЙ вложенной кнопке через контекст. Знак вопроса, стоящий рядом с
     * подписью, оказывается внутри этого поддерева и молча становится вторым
     * триггером поля: у него появляется `aria-haspopup="listbox"`, имя чужого
     * значения, и нажатие открывает не объяснение, а список.
     *
     * Проверено на «Сроке» в очереди проверки: без этой обёртки кнопка
     * получала `aria-labelledby` от значения «любой».
     */
    <ButtonContext.Provider value={{}}>
    <DialogTrigger>
      <Button
        className={styles.infoButton}
        // Имя кнопки называет ПРЕДМЕТ объяснения: «пояснение» без предмета в
        // списке ссылок экрана читается как десять одинаковых кнопок.
        aria-label={t(m.explain, { label })}
      >
        <Glyph size={14} strokeWidth={2} aria-hidden />
      </Button>
      <Popover className={styles.infoPopover} placement="bottom start" offset={4}>
        <Dialog className={styles.infoDialog} aria-label={t(m.explain, { label })}>
          <div className={styles.infoTitle}>{heading ?? label}</div>
          <div>{children}</div>
        </Dialog>
      </Popover>
    </DialogTrigger>
    </ButtonContext.Provider>
  );
}

/**
 * Подпись со знаком вопроса рядом. Общая обвязка для мест, где объяснение
 * обязано быть доступным: знаменатель KPI, вопрос раздела меню, подпись
 * фильтра.
 */
export function Explained({
  children,
  label,
  explain,
  heading,
}: {
  children: ReactNode;
  /** Предмет объяснения для чтения вслух. По умолчанию — сама подпись. */
  label?: string;
  explain?: ReactNode;
  heading?: ReactNode;
}) {
  const t = useT();
  if (!explain) return <>{children}</>;
  return (
    <span className={styles.explained}>
      {children}
      <InfoPopover
        label={label ?? (typeof children === "string" ? children : t(FIELD_TEXT.valueFallback))}
        heading={heading}
      >
        {explain}
      </InfoPopover>
    </span>
  );
}
